import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import { AuthContext } from '../../../Contexts/AuthContext';
import FormDialog from '../../../Components/Modal/modal';
import UseAxios from '../../../Api/http/http';
import {
  getEmployeeVertical,
  createEmployeeVertical,
  updateEmployeeVertical,
} from '../../../Api/request/employeeVertical';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { employeeVerticalTableConfig } from './employeeVerticalTableConfig';
import './employeeVertical.scss';

const EMPTY_FORM = { name: '', isUtilized: false };
const EMPTY_PERMISSIONS = [];

const getApiErrorMessage = (response) => {
  const payload = response?.data;
  if (payload instanceof Error) {
    return payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || payload.message
      || 'Something went wrong.';
  }
  if (payload?.status === false) {
    return payload?.message
      || payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || 'Something went wrong.';
  }
  return '';
};

const normalizeEmployeeVerticalLists = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : response?.data?.data || response?.data?.employeeVerticalList || [];

  return rows.map((employeeVertical) => normalizeEmployeeVertical(employeeVertical));
};

const normalizeEmployeeVertical = (employeeVertical) => ({
  id: employeeVertical.employeeVerticalId,
  name: employeeVertical.employeeVerticalName || '',
  enabledForUtilization: Boolean(employeeVertical.enabledForUtilization) || '',
  isActive: Boolean(employeeVertical.isActive),
});

const EmployeeVerticalForm = ({ mode, form, error, isSubmitDisabled, onChange, onClose, onSubmit }) => createPortal(
  <div className="employee-vertical-modal-backdrop">
    <form className="employee-vertical-modal" onSubmit={onSubmit}>
      <div className="employee-vertical-modal__header">
        {mode === 'edit' ? 'Edit Employee Vertical' : 'Add Employee Vertical'}
      </div>
      <div className="employee-vertical-modal__body">
        <label htmlFor="department-name" className={error ? 'is-error' : ''}>
          Employee Vertical Name<span className="required-asterisk">*</span>
        </label>
        <input
          id="department-name"
          name="name"
          type="text"
          placeholder="Enter Employee Vertical Name"
          value={form.name}
          onChange={onChange}
          className={error ? 'is-error' : ''}
          autoComplete="off"
        />

        <div className="employee-vertical-modal__checkbox-group">
          <input
            id="utilization-status"
            name="isUtilized"
            type="checkbox"
            checked={form.isUtilized}
            onChange={onChange}
          />
          <label htmlFor="utilization-status">
            Utilization Status
          </label>
        </div>
      </div>
      <div className="employee-vertical-modal__footer">
        <button type="button" className="employee-vertical-modal__btn employee-vertical-modal__btn--ghost"
          onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="employee-vertical-modal__btn employee-vertical-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const EmployeeVertical = () => {
  const { userProfile } = React.useContext(AuthContext);
  const permission = userProfile?.permissions?.[MODULE_NAMES.CONFIGURATION_EMPLOYEE_VERTICAL];
  const isSuperAdmin = userProfile?.roleDetails?.some(
    (role) => role.roleName === 'Super Admin',
  );
  const normalizedPermissions = useMemo(
    () => (permission || EMPTY_PERMISSIONS).map((action) => String(action).toLowerCase()),
    [permission],
  );
  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    const actionAliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
    };
    return actionAliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);
  const [ employeeVerticals, setEmployeeVerticals ] = useState([]);
  const [ isLoading, setIsLoading ] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, mode: 'add', id: null });
  const [toggleEmployeeVertical, setToggleEmployeeVertical] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasError, setHasError] = useState(false);

  const loadEmployeeVerticalList = useCallback(async () => {
    if (!hasAccess('read')) return;
    const response = await UseAxios(getEmployeeVertical(), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    setEmployeeVerticals(normalizeEmployeeVerticalLists(response));
  }, [hasAccess]);

  useEffect(() => {
    loadEmployeeVerticalList();
  }, [loadEmployeeVerticalList]);

  const closeForm = useCallback(() => {
    setDialog({ isOpen: false, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, []);

  const openAdd = useCallback(() => {
    if (!hasAccess('create')) return;
    setDialog({ isOpen: true, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, [hasAccess]);

  const openEdit = useCallback((provider) => {
    if (!hasAccess('update')) return;
    setDialog({ isOpen: true, mode: 'edit', id: provider.id });
    setForm({ name: provider.name, isUtilized: provider.enabledForUtilization });
    setHasError(false);
  }, [hasAccess]);

  const selectedEmployeeVertical = employeeVerticals.find((provider) => provider.id === dialog.id);
  const isSubmitDisabled = !form.name.trim()
    || (dialog.mode === 'edit' && (form.name.trim() === selectedEmployeeVertical?.name.trim()
    && form.isUtilized === selectedEmployeeVertical?.enabledForUtilization));

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!hasAccess(dialog.mode === 'edit' ? 'update' : 'create')) return;
    const name = form.name.trim();
    if (!name) {
      setHasError(true);
      toast.error('Please enter a employee vertical name.');
      return;
    }
    const response = dialog.mode === 'edit'
      ? await UseAxios(
        updateEmployeeVertical(dialog.id, name, form.isUtilized, selectedEmployeeVertical?.isActive ?? true),
        setIsLoading,
      )
      : await UseAxios(createEmployeeVertical(name, form.isUtilized), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    toast.success(dialog.mode === 'edit'
      ? 'Employee Vertical updated successfully.'
      : 'Employee Vertical added successfully.');
    const savedEmployeeVertical = normalizeEmployeeVertical(response?.data?.employeeVerticalList);
    setEmployeeVerticals((currentEmployeeVerticals) => (
      dialog.mode === 'edit'
        ? currentEmployeeVerticals.map((eVertical) => (
          eVertical.id === savedEmployeeVertical.id ? savedEmployeeVertical : eVertical
        ))
        : [...currentEmployeeVerticals, savedEmployeeVertical]
    ));
    closeForm();
  }, [closeForm, dialog.id, dialog.mode, form.name, form.isUtilized, hasAccess, selectedEmployeeVertical]);

  const handleToggle = useCallback((eVertical) => {
    if (!hasAccess('update')) return;
    setToggleEmployeeVertical(eVertical);
  }, [hasAccess]);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleEmployeeVertical(null);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleEmployeeVertical || !hasAccess('update')) return;

    const nextStatus = !toggleEmployeeVertical.isActive;
    const response = await UseAxios(
      updateEmployeeVertical(
        toggleEmployeeVertical.id, toggleEmployeeVertical.name, toggleEmployeeVertical.isUtilized, nextStatus),
      setIsLoading,
    );
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setEmployeeVerticals((currentEmployeeVerticals) => currentEmployeeVerticals.map((eVertical) => (
      eVertical.id === toggleEmployeeVertical.id
        ? { ...eVertical, isActive: nextStatus } : eVertical
    )));
    toast.success(`${toggleEmployeeVertical.name} is now ${nextStatus ? 'active' : 'inactive'}.`);
    handleCloseToggleDialog();
  }, [handleCloseToggleDialog, hasAccess, toggleEmployeeVertical]);

  const tableData = useMemo(() => employeeVerticals.map((eVertical, index) => ({
    ...eVertical,
    slNo: index + 1,
    employeeVertical: eVertical.name,
    status: eVertical.isActive ? 'Active' : 'Inactive',
    utilizationStatus: eVertical.enabledForUtilization ? 'Enabled' : 'Disabled',
    action: (
      <div className="employee-vertical-list-actions" onClick={(event) => event.stopPropagation()}>
        <ControlledSwitches
          checked={eVertical.isActive}
          disabled={!hasAccess('update')}
          switchCss="switchCss"
          handleChange={() => handleToggle(eVertical)}
        />
        <button
          type="button"
          className="employee-vertical-list-action-btn"
          title="Edit Employee Vertical"
          aria-label={`Edit ${eVertical.employeeVerticalName}`}
          disabled={!hasAccess('update')}
          onClick={() => openEdit(eVertical)}
        >
          <img src={EditActionIcon} alt="" />
        </button>
      </div>
    ),
  })), [handleToggle, hasAccess, openEdit, employeeVerticals]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {},
      handlers: { handleAddIconClick: openAdd },
      toolBarSettings: {
        showAddNew: hasAccess('create'),
        showFilterOption: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Employee Vertical',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: employeeVerticalTableConfig,
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: employeeVerticalTableConfig.length },
      paginationConfig: { page: 1, limit: tableData.length, total: tableData.length, totalPages: 1 },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [hasAccess, openAdd, tableData.length]);

  if (!hasAccess('read')) {
    return <div style={{marginTop: '30px'}}>You do not have permission to view employee vertical.</div>;
  }

  const onChangeVerticalForm = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setHasError(false);
  };

  return (
    <div className="employee-vertical-list-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialog.isOpen && (
        <EmployeeVerticalForm
          mode={dialog.mode}
          form={form}
          error={hasError}
          isSubmitDisabled={isSubmitDisabled}
          onChange={onChangeVerticalForm}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}
      <FormDialog
        open={Boolean(toggleEmployeeVertical)}
        title={`${toggleEmployeeVertical?.isActive ? 'Disable' : 'Enable'} Employee Vertical`}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {`Are you sure you want to ${toggleEmployeeVertical?.isActive
                ? 'disable' : 'enable'} this employee vertical?`}
            </div>
          </div>
        )}
        button1="Cancel"
        cancelButtonId="cancel-cloud-provider-toggle"
        button2={toggleEmployeeVertical?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="confirm-cloud-provider-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggle}
      />
    </div>
  );
};

export default EmployeeVertical;
